import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { API_URL } from "../../apiConfig";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useTranslation } from "react-i18next";
import { business } from "../../utils/searchMetadata";

const ContactSection = () => {
  const { t } = useTranslation();

  const [formValues, setFormValues] = useState({
    fullName: "",
    emailAddress: "",
    messageText: "",
  });
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  const handleFieldChange = (fieldName) => (event) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: event.target.value,
    }));
  };

  const contactItems = useMemo(
    () => [
      {
        icon: <EmailOutlinedIcon />,
        title: t("Home.Contact.Info.Email.Title", "E-post"),
        value: t("Home.Contact.Info.Email.Value", "post@forsteinntrykk.no"),
        href: "mailto:post@forsteinntrykk.no",
      },
      {
        icon: <PhoneOutlinedIcon />,
        title: t("Home.Contact.Info.Phone.Title", "Telefon"),
        value: t("Home.Contact.Info.Phone.Value", "+47 473 66 224"),
        href: "tel:+4747366224",
      },
      {
        icon: <LocationOnOutlinedIcon />,
        title: t("Home.Contact.Info.Address.Title", "Adresse"),
        value: business.addressLabel,
        href: business.mapsUrl,
      },
      {
        icon: <AccessTimeOutlinedIcon />,
        title: t("Home.Contact.Info.Hours.Title", "Åpningstider"),
        value: t("Home.Contact.Info.Hours.Value", {
          defaultValue: "Normalt mandag–fredag: {{opens}}–{{closes}}. På rolige dager kan vi stenge kl. 15:00. Avtal henting eller ring før du kommer.",
          opens: business.openingHours.opens,
          closes: business.openingHours.closes,
        }),
        href: null,
      },
    ],
    [t],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      await axios.post(`${API_URL}/contact`, formValues);
      setStatus("success");
      setFormValues({ fullName: "", emailAddress: "", messageText: "" });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        t("Home.Contact.Form.ErrorGeneric", "Kunne ikke sende melding. Prøv igjen senere.");
      setErrorMsg(msg);
      setStatus("error");
    }
  };

  return (
    <Box id="contact" sx={{ py: { xs: 7, md: 11 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
          <Typography variant="h3" fontWeight={900} sx={{ mb: 1 }}>
            {t("Home.Contact.Title.Before", "Kontakt")}{" "}
            <Box component="span" sx={{ color: "secondary.dark" }}>
              {t("Home.Contact.Title.Highlight", "Oss")}
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{ opacity: 0.75, maxWidth: 720, mx: "auto" }}
          >
            {t(
              "Home.Contact.Subtitle",
              "Har du spørsmål eller ønsker et tilbud? Ta kontakt med oss!",
            )}
          </Typography>
        </Box>

        <Grid
          container
          spacing={{ xs: 4, md: 6 }}
          alignItems="center"
          textAlign={"start"}
        >
          {/* Left side */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: "grid", gap: 2.6 }}>
              {contactItems.map((item) => (
                <Box
                  key={item.title}
                  component={item.href ? "a" : "div"}
                  href={item.href || undefined}
                  target={item.href?.startsWith("https") ? "_blank" : undefined}
                  rel={
                    item.href?.startsWith("https")
                      ? "noopener noreferrer"
                      : undefined
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    textDecoration: "none",
                    color: "inherit",
                    cursor: item.href ? "pointer" : "default",
                    "&:hover .contactTitle": item.href
                      ? { color: "secondary.main" }
                      : undefined,
                  }}
                >
                  <Box
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: 3,
                      backgroundColor: "rgba(255, 193, 7, 0.16)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "secondary.dark",
                      flexShrink: 0,
                      "& svg": { fontSize: 22 },
                    }}
                  >
                    {item.icon}
                  </Box>

                  <Box>
                    <Typography
                      className="contactTitle"
                      variant="subtitle1"
                      fontWeight={900}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.75 }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* “La oss snakke!” card */}
            <Card
              sx={{
                mt: 6,
                borderRadius: 4,
                minHeight: 140,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0,0,0,0.04)",
                boxShadow: "none",
              }}
            >
              <Typography
                variant="h4"
                fontWeight={900}
                sx={{ color: "secondary.dark" }}
              >
                {t("Home.Contact.LeftCard", "La oss snakke!")}
              </Typography>
            </Card>
          </Grid>

          {/* Right side (form) */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: "0 14px 40px rgba(0,0,0,0.14)",
                p: { xs: 2.5, md: 3.5 },
              }}
            >
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: "grid", gap: 2.4 }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    sx={{ mb: 0.8 }}
                  >
                    {t("Home.Contact.Form.Name", "Navn")}
                  </Typography>
                  <TextField
                    label={t("Home.Contact.Form.Name", "Navn")}
                    autoComplete="name"
                    required
                    fullWidth
                    placeholder={t(
                      "Home.Contact.Form.NamePlaceholder",
                      "Ditt navn",
                    )}
                    value={formValues.fullName}
                    onChange={handleFieldChange("fullName")}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    sx={{ mb: 0.8 }}
                  >
                    {t("Home.Contact.Form.Email", "E-post")}
                  </Typography>
                  <TextField
                    label={t("Home.Contact.Form.Email", "E-post")}
                    autoComplete="email"
                    type="email"
                    required
                    fullWidth
                    placeholder={t(
                      "Home.Contact.Form.EmailPlaceholder",
                      "din@epost.no",
                    )}
                    value={formValues.emailAddress}
                    onChange={handleFieldChange("emailAddress")}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    sx={{ mb: 0.8 }}
                  >
                    {t("Home.Contact.Form.Message", "Melding")}
                  </Typography>
                  <TextField
                    label={t("Home.Contact.Form.Message", "Melding")}
                    required
                    fullWidth
                    multiline
                    minRows={5}
                    placeholder={t(
                      "Home.Contact.Form.MessagePlaceholder",
                      "Skriv din melding her...",
                    )}
                    value={formValues.messageText}
                    onChange={handleFieldChange("messageText")}
                  />
                </Box>

                {status === "success" && (
                  <Alert severity="success">
                    {t("Home.Contact.Form.Success", "Meldingen er sendt! Vi tar kontakt snart.")}
                  </Alert>
                )}
                {status === "error" && (
                  <Alert severity="error">{errorMsg}</Alert>
                )}

                <Button
                  type="submit"
                  disabled={status === "loading"}
                  startIcon={
                    status === "loading" ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SendOutlinedIcon />
                    )
                  }
                  sx={{
                    backgroundColor: "secondary.main",
                    borderRadius: 3,
                    py: 1.5,
                    fontWeight: 900,
                    textTransform: "none",
                    color: "rgba(0,0,0,0.9)",
                  }}
                >
                  {status === "loading"
                    ? t("Home.Contact.Form.Sending", "Sender...")
                    : t("Home.Contact.Form.Submit", "Send melding")}
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactSection;
